<script setup lang="ts">
import BmDialog from "@/components/dialogs/BmDialog.vue";
import type { DialogEvents } from "@/components/mixins/dialogs";
import { useReactiveDialogScriptSetup } from "@/components/mixins/dialogs";
import { computed, toRef, watch } from "vue";
import UnauthorisedComponent from "@/components/pages/UnauthorisedComponent.vue";
import { EnumDialogType } from "@/components/dialogs/DialogTypes";
import type { EnumUserPermission } from "@/app/services/generated/enums/enumUserPermission.generated";
import appStore from "@/store/modules/appStore";
import type { ReadonlyEntityArray } from "@/components/composables/reactivityTypes";

export interface UnauthorisedDialogProps {
  value: boolean;
  requiredPermissions?: ReadonlyEntityArray<EnumUserPermission>;
}
export interface UnauthorisedDialogEvents extends DialogEvents {}

const props = defineProps<UnauthorisedDialogProps>();
const emit = defineEmits<UnauthorisedDialogEvents>();

const { accept, cancel } = useReactiveDialogScriptSetup(
  toRef(props, "value"),
  emit
);

// Join appstore errors with props. Unlikely to occur, but safe to join all
const joinedUserPermissionRequirements = computed(() => {
  const allErrors = [...(props.requiredPermissions ?? [])];

  if (appStore.state.unauthorisedError) {
    allErrors.push(appStore.state.unauthorisedError);
  }

  return allErrors;
});

/**
 * Watch for appStore unauthorised errors to manually pop-up the dialog
 *
 * We use this approach as showModalDialog would open n number of dialogs, and this matches
 * how our existing error dialog works.
 */
watch(
  () => appStore.state.unauthorisedError,
  () => {
    emit("input", appStore.state.unauthorisedError !== null);
  },
  { immediate: true }
);

function acceptAndReset() {
  appStore.clearUnauthorisedError();
  accept();
}
</script>

<template>
  <bm-dialog
    :label="$m.unauthorised"
    :visible="value"
    :dialog-type="EnumDialogType.none"
    :cancelable="false"
    width="1200"
    @accept="acceptAndReset"
    @cancel="acceptAndReset"
  >
    <v-container fluid pa-0>
      <v-row>
        <v-col cols="12">
          <unauthorised-component
            :required-permissions="joinedUserPermissionRequirements"
          />
        </v-col>
      </v-row>
    </v-container>
    <template #controls-confirm-text>
      {{ $m.ok }}
    </template>
  </bm-dialog>
</template>
